/**
 * Optimized scroll animation system for smooth performance
 */

class OptimizedScrollAnimations {
  private observer: IntersectionObserver | null = null;
  private animatedElements = new WeakSet<Element>();
  private rafId: number | null = null;
  private pendingEntries: IntersectionObserverEntry[] = [];

  constructor() {
    this.init();
  }

  private init() {
    // Only initialize if IntersectionObserver is supported
    if (typeof IntersectionObserver === 'undefined') return;

    // Create a single observer for all elements with optimized settings
    this.observer = new IntersectionObserver(
      (entries) => {
        // Batch updates in the next animation frame
        this.pendingEntries.push(...entries);
        
        if (!this.rafId) {
          this.rafId = requestAnimationFrame(() => {
            this.processPendingEntries();
            this.rafId = null;
          });
        }
      },
      {
        // Less aggressive observation
        rootMargin: '50px 0px', // Start animating a bit before element is visible
        threshold: 0.01, // Minimal threshold for better performance
      }
    );

    // Start observing elements
    this.observeElements();
  }

  private processPendingEntries() {
    // Process all pending entries at once
    const toAnimate: Element[] = [];
    
    for (const entry of this.pendingEntries) {
      if (entry.isIntersecting && !this.animatedElements.has(entry.target)) {
        toAnimate.push(entry.target);
        this.animatedElements.add(entry.target);
        // Stop observing after animation to reduce overhead
        this.observer?.unobserve(entry.target);
      }
    }

    // Clear pending entries
    this.pendingEntries = [];

    // Apply animations in a single batch
    if (toAnimate.length > 0) {
      // Use CSS containment to improve performance
      toAnimate.forEach(element => {
        const htmlElement = element as HTMLElement;
        
        // Get delay from data attribute or use default
        const delay = htmlElement.dataset.delay || '0s';
        
        // Apply animation with a slight delay
        setTimeout(() => {
          htmlElement.classList.add('is-visible');
          // Remove animation classes after animation completes to free up memory
          setTimeout(() => {
            htmlElement.classList.remove('animate-on-scroll');
            htmlElement.style.transition = 'none';
          }, 600); // Slightly longer than animation duration
        }, parseFloat(delay) * 1000);
      });
    }
  }

  private observeElements() {
    // Use requestIdleCallback for non-critical initialization
    const observe = () => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach(element => {
        // Skip if already animated
        if (!this.animatedElements.has(element)) {
          this.observer?.observe(element);
        }
      });
    };

    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(observe, { timeout: 500 });
    } else {
      setTimeout(observe, 100);
    }
  }

  public refresh() {
    // Re-observe new elements (useful for dynamic content)
    this.observeElements();
  }

  public destroy() {
    // Clean up
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    this.observer?.disconnect();
    this.observer = null;
    this.pendingEntries = [];
  }
}

// Create singleton instance
let instance: OptimizedScrollAnimations | null = null;

export const initScrollAnimations = () => {
  if (!instance) {
    instance = new OptimizedScrollAnimations();
  }
  return instance;
};

export const refreshScrollAnimations = () => {
  instance?.refresh();
};

export const destroyScrollAnimations = () => {
  instance?.destroy();
  instance = null;
};
